import api from './axiosInstance';

export interface DocumentMaster {
  _id: string;
  id?: string;
  documentName: string;
  documentDescription?: string;
  status: 'Active' | 'Inactive';
  createdBy?: string | { id: string; name: string; email: string };
  updatedBy?: string | { id: string; name: string; email: string };
  createdAt?: string;
  updatedAt?: string;
}

export interface DocumentMasterFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  status?: 'Active' | 'Inactive';
  documentName?: string;
}

export interface DocumentMasterDropdownOption {
  value: string;
  label: string;
}

class DocumentMasterService {
  /**
   * Get all document masters without pagination (for dropdowns)
   * Filters by Active status by default
   */
  async getAllDocumentMasters(filters: Omit<DocumentMasterFilters, 'page' | 'limit'> = {}) {
    try {
      const params = new URLSearchParams();
      
      // Default to Active status if not specified
      const status = filters.status || 'Active';
      params.append('status', status);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.documentName) params.append('documentName', filters.documentName);
      if (filters.sortBy) params.append('sort-by', filters.sortBy);
      if (filters.sortOrder) params.append('sort-order', filters.sortOrder);

      const response = await api.get(`/document-masters?${params.toString()}`);
      
      // Handle different response structures
      if (response.data && typeof response.data === 'object') {
        if ('data' in response.data && Array.isArray(response.data.data)) {
          return response.data.data;
        } else if (Array.isArray(response.data)) {
          return response.data;
        }
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching document masters:', error);
      throw error;
    }
  }

  /**
   * Get document masters with pagination
   */
  async getDocumentMasters(filters: DocumentMasterFilters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.sortBy) params.append('sort-by', filters.sortBy);
      if (filters.sortOrder) params.append('sort-order', filters.sortOrder);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.documentName) params.append('documentName', filters.documentName);

      const response = await api.get(`/document-masters?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching document masters:', error);
      throw error;
    }
  }

  /**
   * Get a single document master by ID
   */
  async getDocumentMasterById(id: string) {
    try {
      const response = await api.get(`/document-masters/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching document master:', error);
      throw error;
    }
  }

  /**
   * Create a new document master
   */
  async createDocumentMaster(data: {
    documentName: string;
    documentDescription?: string;
    status?: 'Active' | 'Inactive';
  }) {
    try {
      const response = await api.post('/document-masters', data);
      return response.data;
    } catch (error) {
      console.error('Error creating document master:', error);
      throw error;
    }
  }

  /**
   * Update a document master
   */
  async updateDocumentMaster(id: string, data: {
    documentName?: string;
    documentDescription?: string;
    status?: 'Active' | 'Inactive';
  }) {
    try {
      const response = await api.put(`/document-masters/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating document master:', error);
      throw error;
    }
  }

  /**
   * Inactivate a document master (soft delete)
   */
  async inactivateDocumentMaster(id: string) {
    try {
      const response = await api.patch(`/document-masters/${id}/inactivate`);
      return response.data;
    } catch (error) {
      console.error('Error inactivating document master:', error);
      throw error;
    }
  }

  /**
   * Activate a document master
   */
  async activateDocumentMaster(id: string) {
    try {
      const response = await api.patch(`/document-masters/${id}/activate`);
      return response.data;
    } catch (error) {
      console.error('Error activating document master:', error);
      throw error;
    }
  }

  /**
   * Get document masters as dropdown options
   * Returns only Active document masters
   */
  async getDocumentMasterDropdownOptions(): Promise<DocumentMasterDropdownOption[]> {
    try {
      const documentMasters = await this.getAllDocumentMasters({ status: 'Active' });
      return documentMasters.map((doc: DocumentMaster) => ({
        value: doc.documentName,
        label: doc.documentName,
      }));
    } catch (error) {
      console.error('Error fetching document master dropdown options:', error);
      return [];
    }
  }
}

const documentMasterService = new DocumentMasterService();
export default documentMasterService;
